import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useDeepCompareEffect } from 'react-use'

import { NAV_BRAND, SEARCH, SWITCHES } from '@/config'
import { PORTAL_TYPE_RJA, PORTAL_TYPE_YF } from '@/config/constants'
import { RJA_HOME } from '@/page/constants'
import { getPageType } from '@/page/store/selectors'
import { NEW_SEARCH_TRIGGER_MANUAL, newSearch } from '@/results/store/actions'

import { api } from '../../services'
import { trackCurrentLocationAllow, trackCurrentLocationReject, trackCurrentLocationRequest, trackCurrentLocationSuccess } from '../../tracking/utils/tracking'
import { CURRENT_LOCATION_PENDING, CURRENT_LOCATION_SUGGESTION, DISTANCES, SEARCH_FORM_SETTINGS } from '../constants'
import useSearchIcons from '../hooks/useSearchIcons'
import { setSearchQuery, changeSearchValue, showFirstResult } from '../store/actions'
import { getReducedSearchState, getSearchQuery, getSearchType } from '../store/selectors'

import SearchAutosuggest, { renderHighlightedSuggestion } from './SearchAutosuggest'

const searchBtnLabelsMapping = {
  [PORTAL_TYPE_YF]: 'Jetzt Job finden',
  [PORTAL_TYPE_RJA]: 'Suchen'
}

const SearchBarButton = ({
  icon,
  label,
  classNames,
  faClassNames,
  disabled
}) => {
  return (
    <button
      type='submit'
      className={classnames('btn btn-primary btn-search qa-search-btn', classNames)}
      disabled={disabled}
    >
      <FontAwesomeIcon icon={icon} className={classnames('btn-icon', faClassNames)} />
      {label && <span className={classnames('btn-text', { 'sr-only': !label })}>{label}</span>}
    </button>
  )
}

SearchBarButton.propTypes = {
  icon: PropTypes.object,
  label: PropTypes.string,
  classNames: PropTypes.string,
  faClassNames: PropTypes.string,
  disabled: PropTypes.bool
}

const SearchForm = ({ onSubmitAction }) => {
  const dispatch = useDispatch()
  const query = useSelector(getSearchQuery)
  const type = useSelector(getSearchType)
  const searchParams = useSelector(getReducedSearchState)

  const distances = DISTANCES
  const extraParams = SEARCH.extraParams
  const formAction = SEARCH.types[type].location

  const { portal_type: portalType } = NAV_BRAND
  const { current: searchBtnLabel } = useRef(searchBtnLabelsMapping[portalType] || null)
  const { current: isRja } = useRef(portalType === PORTAL_TYPE_RJA)
  const { current: isYF } = useRef(portalType === PORTAL_TYPE_YF)

  const isRjaHome = useSelector((state) => getPageType(state) === RJA_HOME)

  const { searchTermIcon, searchLocationIcon, searchBtnIcon, clearIcon } = useSearchIcons()

  const { namePlaceholder, locationPlaceholder, searchAutocompleteType } = SEARCH_FORM_SETTINGS[type]

  const [name, setName] = useState(query.name)
  const [location, setLocation] = useState(query.location || window.sessionStorage.getItem('currentLocation') || '')
  const [currentLocation, setCurrentLocation] = useState(query.location || window.sessionStorage.getItem('currentLocation') || '')
  const [backupLocation, setBackupLocation] = useState(location)
  const [dist, setDist] = useState(query.dist)
  const [displayUseCurrentLocation, setDisplayUseCurrentLocation] = useState(true)
  const [useSuggestedValue, setUseSuggestedValue] = useState(false)
  const formState = useMemo(() => ({ name, location, dist }), [name, location, dist])
  const formRef = useRef()
  const submitDisabled = useMemo(() => location === CURRENT_LOCATION_PENDING, [location])

  const registerFormElement = useCallback((ref) => {
    formRef.current = ref
  }, [])

  const updateInternalStateIfDifferent = useCallback((newQuery) => {
    if (name !== newQuery.name) {
      setName(newQuery.name)
    }
    if (location !== newQuery.location) {
      setLocation(newQuery.location)
    }
    if (dist.value !== newQuery.dist) {
      setDist(newQuery.dist)
    }
  }, [dist, location, name])

  const onInputChange = useCallback(({ target }, { newValue } = {}, setValue) => {
    dispatch(changeSearchValue(newValue))
    if (target.value !== newValue) {
      setValue(newValue)
    }
  }, [dispatch])

  const onSubmit = useCallback((event) => {
    // additional onSubmit action
    if (onSubmitAction) {
      onSubmitAction()
    }
    // if integrated search is disabled, the search form must be allowed to submit
    // i.e. don't event.preventDefault()
    if (!SWITCHES.integratedSearch) {
      if (formRef.current) {
        // needs to update value on input before submit
        setTimeout(() => formRef.current.submit())
        return
      }
      return
    }
    // show first result after applying search
    dispatch(showFirstResult(true))
    // update the form with the newly computed params
    dispatch(setSearchQuery({ ...formState }, { forceSearch: true }))
    // trigger new search
    dispatch(newSearch.trigger({ ...searchParams, ...formState }, { trigger: NEW_SEARCH_TRIGGER_MANUAL }))
    // cancel the form submit
    event && event.preventDefault()
  }, [formState, onSubmitAction, dispatch, searchParams])

  const closeKeyboardAndUnfocus = useCallback((event) => {
    // unfocus the input field and close soft keyboard if we can
    if (event.target && typeof event.target.querySelector === 'function') {
      const el = event.target.querySelector(':focus')
      if (el && typeof el.blur === 'function') {
        el.blur()
      }
    }
  }, [])

  const onNameChange = useCallback((event, { newValue }) => {
    onInputChange({ target: { name: 'name' } }, { newValue }, setName)
  }, [onInputChange])

  const onLocationChange = useCallback((event, { newValue }) => {
    onInputChange({ target: { name: 'location' } }, { newValue }, setLocation)
    if (![CURRENT_LOCATION_PENDING, CURRENT_LOCATION_SUGGESTION].includes(newValue)) {
      setBackupLocation(newValue)
    }
  }, [onInputChange])

  const onDistChange = useCallback((event) => {
    const value = parseInt(event.target.value)
    dispatch(changeSearchValue(value))
    if (dist.value !== value) {
      setDist(value)
      setUseSuggestedValue(true)
    }
    closeKeyboardAndUnfocus(event)
  }, [closeKeyboardAndUnfocus, dispatch, dist])

  const onClearName = useCallback((event) => (
    onNameChange(event, { newValue: '' })
  ), [onNameChange])

  const onClearLocation = useCallback((event) => (
    onLocationChange(event, { newValue: '' })
  ), [onLocationChange])

  const onNameSuggestionSelected = useCallback((event, { suggestion }) => {
    setUseSuggestedValue(true)
    setName(suggestion)
  }, [])

  const onLocationSuggestionSelected = useCallback((event, { suggestion }) => {
    if (suggestion === CURRENT_LOCATION_SUGGESTION && displayUseCurrentLocation) {
      onLocationChange(event, { newValue: CURRENT_LOCATION_PENDING || '' })
      trackCurrentLocationRequest()
      navigator.geolocation.getCurrentPosition(({ coords: { latitude, longitude } }) => {
        trackCurrentLocationAllow()
        const response = api.getRegionByGeolocation({ latitude, longitude })
        response.then(({ status, data: { status: dataStatus, results } }) => {
          if (status === 200 && dataStatus === 'OK') {
            trackCurrentLocationSuccess()
            setDisplayUseCurrentLocation(false)
            const cityAddress = results[0].address_components.find((result) => result.types[0] === 'locality')
            const cityName = cityAddress?.long_name || ''
            onLocationChange(event, { newValue: cityName })
            window.sessionStorage.setItem('currentLocation', cityName)
            setCurrentLocation(cityName)
          } else {
            onLocationChange(event, { newValue: backupLocation || '' })
          }
        })
      }, () => {
        trackCurrentLocationReject()
        onLocationChange(event, { newValue: backupLocation || '' })
      })
    } else {
      onLocationChange(event, { newValue: suggestion })
    }
  }, [backupLocation, displayUseCurrentLocation, onLocationChange])

  useEffect(() => {
    if (useSuggestedValue) {
      setUseSuggestedValue(false)
      onSubmit()
    }
  }, [useSuggestedValue, dist, onSubmit])

  useDeepCompareEffect(() => {
    updateInternalStateIfDifferent(query)
  }, [query])

  const searchFocusHandler = useCallback(() => {
    if (submitDisabled) {
      setLocation('')
    }
  }, [submitDisabled])

  return (
    <form
      className='form-search qa-search-form'
      method='get'
      onSubmit={onSubmit}
      action={formAction}
      ref={registerFormElement}
    >
      <input type='hidden' name='plz' value={`${location}:${dist}`} />
      {Object.entries(extraParams).map(([key, value]) =>
        <input key={key} type='hidden' name={key} value={value} />
      )}
      <div className={classnames('d-flex flex-column flex-md-row flex-fill inputs-container', { 'rja-home': isRjaHome })}>
        <div
          className={classnames('input-group flex-fill', { empty: !name })}
        >
          <div className='input-group-prepend'>
            <span className='input-group-text'>
              <FontAwesomeIcon icon={searchTermIcon} />
            </span>
          </div>
          <SearchAutosuggest
            type='search'
            name='name'
            searchType={searchAutocompleteType}
            className='form-control qa-search-term'
            placeholder={namePlaceholder}
            value={name}
            onChange={onNameChange}
            onSuggestionSelected={onNameSuggestionSelected}
            aria-label={namePlaceholder}
            onFocus={searchFocusHandler}
          />
          {Boolean(name) &&
            <div className='input-group-append'>
              <div className='input-group-clear' onClick={onClearName}>
                <FontAwesomeIcon icon={clearIcon} />
              </div>
            </div>}
          {isRja &&
            <>
              <div className='input-group-divider tablet' />
              <SearchBarButton
                classNames={classnames('mobile', { 'rja-home': isRjaHome })}
                icon={searchBtnIcon}
                label={searchBtnLabel}
                faClassNames={classnames({ 'fa-fw': !isYF })}
                disabled={submitDisabled}
              />
            </>}
        </div>
        <div
          className={classnames('input-group flex-fill dist', { empty: !location })}
        >
          <div className='input-group-prepend'>
            <span className='input-group-text'>
              <FontAwesomeIcon icon={searchLocationIcon} />
            </span>
          </div>
          <SearchAutosuggest
            type='search'
            name='location'
            searchType='location'
            className='form-control qa-search-location'
            placeholder={locationPlaceholder}
            value={location}
            onChange={onLocationChange}
            onSuggestionSelected={onLocationSuggestionSelected}
            aria-label={locationPlaceholder}
            label='Location suggestions'
            predefinedSuggestions={displayUseCurrentLocation
              ? [CURRENT_LOCATION_SUGGESTION]
              : (currentLocation ? [currentLocation] : [])}
            shouldRenderSuggestions={() => true}
            renderSuggestion={(suggestion, ...params) => {
              if (suggestion === CURRENT_LOCATION_SUGGESTION) {
                return <div className='current-location-autosuggest'>{suggestion}</div>
              }
              if (suggestion === currentLocation && !displayUseCurrentLocation) {
                return <div className='current-location-autosuggest search-highlight'>{`${suggestion} (aktueller Standort)`}</div>
              }
              return renderHighlightedSuggestion(suggestion, ...params)
            }}
            fetchSuggestionsBlackList={[CURRENT_LOCATION_SUGGESTION, CURRENT_LOCATION_PENDING]}
          />
          {Boolean(location) &&
            <div className='input-group-append'>
              <div className='input-group-clear' onClick={onClearLocation}>
                <FontAwesomeIcon icon={clearIcon} />
              </div>
            </div>}
          <div className='input-group-divider' />
          <div className='distance-section'>
            <select
              name='dist'
              className='custom-select qa-search-distance'
              value={dist}
              onChange={onDistChange}
              aria-label='Umkreis'
            >
              {distances.map(distance => (
                <option key={distance} value={distance}>{distance}km</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <SearchBarButton
        classNames={classnames('tablet', { 'rja-home': isRjaHome })}
        icon={searchBtnIcon}
        label={searchBtnLabel}
        faClassNames={classnames({ 'fa-fw': !isYF })}
        disabled={submitDisabled}
      />
    </form>
  )
}

SearchForm.propTypes = {
  onSubmitAction: PropTypes.func
}

export default SearchForm
